import { Autocomplete, Button, Container, Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Slider, TextField, ThemeProvider, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import cytoscape, { ElementDefinition } from 'cytoscape';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import "./cytokine.css";
import { Footer } from './Footer';
import { Header } from './header';
import { COLOR_DARK, COLOR_LIGHT, PURPLE, theme, YELLOW } from './theme';
import { createRoot } from 'react-dom/client';
import { CSVLink } from "react-csv";
let popper = require('cytoscape-popper');

const cytoscapeFcose = require('cytoscape-fcose');

cytoscape.use(popper);
cytoscape.use(cytoscapeFcose);

const layout = {
  name: 'fcose',
  fit: true,
  tile: true,
  uniformNodeDimensions: true,
  nodeSeparation: 500,
  idealEdgeLength: 100
};

const defaultStyleSheet: Array<any> = [
  {
    selector: "node",
    style: {
      backgroundColor: "dimgray",
      width: 10,
      height: 10,
      label: "data(shortSymbol)",
      "z-index": 10,
      "text-outline-color": "#FFFFFF",
      "text-outline-width": "2px",
      fontSize: "6px",
      color: COLOR_DARK,
    }
  },
  {
    selector: "node.clicked",
    style: {
      "border-width": "6px",
      "border-color": "#AAD8FF",
      "border-opacity": 0.5,
      width: 15,
      height: 15,
    }
  },
  {
    selector: "node[kinase='True']",
    style: {
      shape: "square"
    }
  },
  {
    selector: "node[inKeyFunctional='True']",
    style: {
      "background-color": PURPLE,
    }
  },
  {
    selector: "node[?selected]",
    style: {
      "background-color": YELLOW,
    }
  },
  {
    selector: "edge",
    style: {
      width: 1,
      "line-color": COLOR_DARK,
      "target-arrow-color": COLOR_DARK,
      "target-arrow-shape": "triangle",
      "curve-style": "bezier",
      'line-style': 'solid'
    }
  },
  {
    selector: "edge.connectedEdge",
    style: {
      "z-index-compare": "manual",
      "z-index": 1000,
      width: 2
    }
  },
  {
    selector: "edge[subtype1='activation'], [subtype1='expression']",
    style: {
      "line-color": "#b6d7a8",
      "target-arrow-color": "#b6d7a8",
    }
  },
  {
    selector: "edge[subtype1='inhibition'], [subtype1='repression']",
    style: {
      "line-color": COLOR_LIGHT,
      "target-arrow-color": COLOR_LIGHT,
    }
  },
  {
    selector: "edge[type='String']",
    style: {
      "line-color": COLOR_DARK,
      "target-arrow-color": COLOR_DARK,
    }
  },
  {
    selector: "edge[type='GErel']",
    style: {
      'line-style': 'dashed'
    }
  },
  {
    selector: "edge[type='PPrel']",
    style: {
      'line-style': 'solid'
    }
  },
  {
    selector: "edge[type='String']",
    style: {
      'line-style': 'dotted'
    }
  },
  {
    selector: "edge[subtype1='indirect effect'], [subtype2 ='indirect effect']",
    style: {
      'line-style': 'dotted'
    }
  },
  {
    selector: ".hideme",
    style: {
      visibility: "hidden",
    }
  },
  {
    selector: ".grayOut",
    style: {
      "line-color": "gray",
      "target-arrow-color": "gray",
      opacity: 0.2,
      "z-compound-depth": "bottom",
      backgroundColor: "gray",
      color: "gray",
      'text-opacity': 0.2,
    }
  }
];

export interface ReferenceType { label: string, id: string };
export interface GeneType {
  gene: string,
  geneid: string,
  genetype: string,
  entrezgene: number,
  name: string,
  alias: string,
  HGNC: string,
};

function CytokineLegend(): ReactElement {
  return (
    <List>
      <ListItem disablePadding>
        <ListItemText primaryTypographyProps={{ variant: 'body1' }} primary="Legend" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon className="square" sx={{ backgroundColor: "dimgray", border: "dimgray" }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Kinase" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon className="circle" style={{ backgroundColor: PURPLE, border: PURPLE }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Node of Interest" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon className="circle" style={{ backgroundColor: YELLOW, border: YELLOW }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Selected Cytokine" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon sx={{ border: "2px solid #b6d7a8" }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Activation" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon sx={{ border: `2px solid ${COLOR_LIGHT}` }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Inhibition" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon className="line" sx={{ border: `2px dashed ${COLOR_DARK}` }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="GErel" />
      </ListItem>
      <ListItem disablePadding>
        <ListItemIcon sx={{ border: `2px solid ${COLOR_DARK}` }}>
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="PPI" />
      </ListItem>
    </List>
  )
}

function GeneInformation({ selectedGenes }: { selectedGenes: Array<GeneType> }): ReactElement {
  return (
    <>
      <Typography gutterBottom variant='body1'>
        Gene Information
      </Typography>
      {selectedGenes.length === 0 ? <Typography variant='body2' color="secondary" sx={{ paddingBottom: "12px" }}>
        Select a cytokine or kinase from the drop down list
      </Typography>
        : selectedGenes.map((gene: GeneType) =>
          <List key={gene.gene}>
            <ListItem disablePadding key={gene.gene}>
              <ListItemText primaryTypographyProps={{ variant: 'body2', style: { fontWeight: 550 } }} primary={`Gene: ${gene.gene}`} />
            </ListItem>
            <ListItem disablePadding key={gene.geneid}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`ID: ${gene.geneid}`} />
            </ListItem>
            <ListItem disablePadding key={gene.genetype}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`Type: ${gene.genetype}`} />
            </ListItem>
            <ListItem disablePadding key={gene.entrezgene}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`Entrezgene: ${gene.entrezgene}`} />
            </ListItem>
            <ListItem disablePadding key={gene.name}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`Name: ${gene.name}`} />
            </ListItem>
            <ListItem disablePadding key={gene.alias}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`Alias: ${gene.alias}`} />
            </ListItem>
            <ListItem disablePadding key={gene.HGNC}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`HGNC: ${gene.HGNC}`} />
            </ListItem>
          </List>
        )
      }
    </>
  )
}

export function Cytokines() {
  const cyRef = useRef<cytoscape.Core | undefined>();
  const cyPopperRef = useRef<cytoscape.Core | undefined>();

  const [symbols, setSymbols] = useState<Array<ReferenceType>>([]);
  const [symbol, setSymbol] = useState<ReferenceType | null>(null);
  const [graphData, setGraphData] = useState<ElementDefinition[]>([]);
  const [weightRange, setWeightRange] = useState<number[]>([0, 10]);
  const [sliderMin, setSliderMin] = useState<number>(0);
  const [sliderMax, setSliderMax] = useState<number>(10);
  const [allGenes, setAllGenes] = useState<Array<GeneType>>([]);
  const [selectedGenes, setSelectedGenes] = useState<Array<GeneType>>([]);

  const [edgesCSV, setEdgesCSV] = useState<any[]>([])
  const [nodesCSV, setNodesCSV] = useState<any[]>([])
  const edgesCSVLink = useRef<any | null>(null);
  const nodesCSVLink = useRef<any | null>(null);

  // First Load Get Options and all Gene information
  useEffect(() => {
    (async () => {
      const references = await axios.get(`/kincyte/data/reference.json`);
      const options = references.data.map((v: any): ReferenceType => ({ label: v.cytokine, id: v.symbol }));
      setSymbols(options);
      setSymbol(options[0]);

      const allGenes = await axios.get(`/kincyte/data/gene-data.json`);
      setAllGenes(allGenes.data)
    })();
  }, []);

  // When Option Changes Load + Prep Graph
  useEffect(() => {
    if (!symbol) return;
    setGraphData([]);
    (async () => {
      const data = await Promise.all([
        axios.get(`/kincyte/data/cytokine-jsons/node_${symbol.id}.json`),
        axios.get(`/kincyte/data/cytokine-jsons/edges_${symbol.id}.json`),
      ]);

      // Add Selected State To Node Rather Than Node To Stylesheet
      // Add longTitle and shortSymbol to Node
      const nodes = data[0].data.map((v: any) => ({
        data: Object.assign(v, {
          selected: v.symbol === symbol.label,
          longTitle: v.symbol.length > 8,
          shortSymbol: v.symbol.length > 8 ? `${v.symbol.substring(0, 8)}...` : v.symbol,
        })
      }));
      const edges = data[1].data.map((v: any) => ({
        data: {
          'source': v.source,
          'target': v.target,
          'type': v.type,
          'subtype1': v.subtype1,
          'subtype2': v.subtype2,
          'subtype3': v.subtype3,
          'subtype4': v.subtype4,
          'weight': v.weight,
        }
      }));

      const min = Math.min(...edges.map((v: any) => v.data.weight))
      const max = Math.max(...edges.map((v: any) => v.data.weight))
      setSliderMin(min);
      setSliderMax(max);
      setWeightRange([min, max])

      setGraphData(CytoscapeComponent.normalizeElements({
        nodes,
        edges
      }));

      setNodesCSV(nodes.map((v: any) => v.data))
      setEdgesCSV(edges.map((v: any) => v.data).map((obj: any) => { const { id, ...newObj } = obj; return newObj }))

      cyRef.current?.center()
      cyRef.current?.layout(layout).run()
    })();
  }, [symbol])

  const defineListeners = useCallback((cy: any) => {
    cy.unbind("tap");

    // Click on a node to highlight connections
    cy.on("tap", "node", function (event: any) {
      // Remove any existing selection & gene information
      cy.edges().removeClass("connectedEdge").removeClass("grayOut");
      cy.nodes().removeClass("grayOut");
      setSelectedGenes([]);

      // Clear map if re-clicking the same node
      if (event.target.classes().includes("clicked")) {
        cy.nodes().removeClass("clicked");
        return
      }
      // Set new gene information
      cy.nodes().removeClass("clicked");
      const target_list = event.target.id().split(", ");
      const selectedGenes = allGenes.filter((v: any) => target_list.includes(v.gene));
      setSelectedGenes(selectedGenes);

      // Highlight node & edges
      event.target.connectedEdges().toggleClass("connectedEdge");
      event.target.toggleClass("clicked");
      // gray out non-connected nodes
      cy.nodes().not(event.target.closedNeighborhood()).toggleClass("grayOut");
      cy.edges().not(event.target.closedNeighborhood()).toggleClass("grayOut");

      event.target.connectedEdges().connectedNodes('[id!=\'' + event.target.id() + '\']').forEach((node: any) => {
        const edges = event.target.edgesWith(node)
        if (edges.length === 1 && edges.classes().includes('hideme')) {
          // gray out nodes that are connected through 1 hidden edge
          node.toggleClass("grayOut")
        }
        if (edges.length > 1) {
          const hidden = edges.map((edge: any) => edge.classes().includes('hideme'))
          if (hidden.includes(false)) {
            // don't gray out nodes that are connected through hidden & unhidden edges
            node.removeClass("grayOut")
          }
          else {
            // gray out nodes that are connected through all hidden edges
            node.addClass("grayOut")
          }
        }
      })
    });

    // Click out of graph to remove highlight
    cy.on('tap', function (event: any) {
      if (event.target === cy) {
        cy.edges().removeClass("connectedEdge").removeClass("grayOut");
        cy.nodes().removeClass("clicked").removeClass("grayOut");
        setSelectedGenes([])
      }
    });

    // Hover over to get full name
    cy.nodes('[?longTitle]').unbind("mouseover");
    cy.nodes('[?longTitle]').bind("mouseover", (event: any) => {
      const currentNode = event.target;

      if (currentNode.hasClass('grayOut')) return

      const fontFamily = currentNode.style().fontFamily
      const fontSize = cy.zoom() * currentNode.numericStyle('font-size')
      event.target.popperRefObj = event.target.popper({
        content: () => {
          const component = <Typography
            className="target-popper"
            style={{
              fontSize: fontSize.toString() + 'px', fontFamily: fontFamily, paddingLeft: "0.5em", paddingRight: "0.5em", maxWidth: "20vw"
            }}
          >
            {currentNode.data().symbol}
          </Typography>;
          const container = document.createElement('div');
          const root = createRoot(container);
          root.render(component);
          document.body.appendChild(container);
          return container;
        },
        popper: {
          placement: 'top-start',
          removeOnDestroy: true,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-(fontSize * 2), 0],
              },
            },
          ]
        },
      });
    });

    cy.nodes('[?longTitle]').unbind("mouseout");
    cy.nodes('[?longTitle]').bind("mouseout", (event: any) => {
      if (event.target.popper && !event.target.hasClass('grayOut')) {
        event.target.popperRefObj.state.elements.popper.remove();
        event.target.popperRefObj.destroy();
      }
    });

    cy.nodes('[?longTitle]').unbind("grabon");
    cy.nodes('[?longTitle]').on('grabon', (event: any) => {
      if (event.target.popper && !event.target.hasClass('grayOut')) {
        event.target.popperRefObj.state.elements.popper.remove();
        event.target.popperRefObj.destroy();
      }
    });

    cy.unbind("zoom");
    cy.on('zoom', (event: any) => {
      event.target.elements().forEach((v: any) => {
        if (v.popperRefObj) {
          v.popperRefObj.state.elements.popper.remove();
          v.popperRefObj.destroy();
        }
      })
    });

  }, [allGenes])

  const onChange = useCallback((e: Event, value: number | number[]) => {
    setWeightRange(value as number[]);
  }, [])

  const onChangeCommitted = (e: any, value: number | number[]) => {
    cyRef.current?.startBatch();
    // Removes edges
    cyRef.current?.edges().forEach(el => {
      const weight = el.data("weight");
      const show = (weight >= weightRange[0] && weight <= weightRange[1]);
      if (!show) el.addClass("hideme");
      else el.removeClass("hideme");

      // If the edge has been removed and it was connected to a clicked node, gray out other node
      const clickedNodeID = cyRef.current?.filter(".clicked").id();
      const clickedNodeObj = cyRef.current?.nodes('[id=\'' + clickedNodeID + '\']')[0]
      const newNode = el.connectedNodes().filter('[id!=\'' + clickedNodeID + '\']')
      if (clickedNodeObj) {
        if (clickedNodeObj.connectedEdges().contains(el) && el.hasClass("hideme")) {
          newNode.addClass("grayOut")
        }
        else if (clickedNodeObj.connectedEdges().contains(el) && !el.hasClass("hideme")) {
          newNode.removeClass("grayOut")
        }
      }

    })
    // Remove stranded nodes
    cyRef.current?.nodes().forEach(el => {
      if (el.connectedEdges().allAre(".hideme")) el.addClass("hideme");
      else el.removeClass("hideme");
    })

    defineListeners(cyRef.current)

    cyRef.current?.endBatch();
    setGraphData(CytoscapeComponent.normalizeElements(graphData));
  }

  return (
    <ThemeProvider theme={theme}>
      <Container >
        <Grid container spacing={2}>
          <Header label="Cytokine Explorer" home={true} />
          <Grid item xs={12} sm={8}>
            <Typography variant='body1' style={{ paddingBottom: '30px' }}>
              Enter the name of a cytokine to explore a network showing the potential interaction and connections between a cytokine and its potential upstream kinases based on the KEGG pathway database. The network displays square nodes for kinases, round nodes for other genes, yellow for the cytokine of interest, and purple for its corresponding kinases. The edge colors show the type of interaction, with the weight indicating the difficulty of that interaction. Click on a node to highlight the network centered around that node and its closest neighbors. Hover over a node to get a more detailed gene annotation.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} alignContent="baseline" >
            <Typography variant='h2' sx={{ mb: 2 }}>Search</Typography>
            <Autocomplete
              value={symbol}
              options={symbols}
              multiple={false}
              renderInput={(params) => <TextField {...params} label="Symbol" />}
              onChange={(e, v) => { setSymbol(v) }}
            />
          </Grid>
        </Grid>
        {(!graphData || graphData.length === 0) && (
          <Grid container>
            <Typography variant='body2' color="secondary">
              Select a cytokine or kinase from the drop down list
            </Typography>
          </Grid>
        )}
      </Container >
      {(graphData && !!graphData.length) && (
        <Grid container spacing={2} sx={{ height: "fit-content", position: "relative" }} >
          <Grid item xs={12} md={10} sx={{ paddingBottom: 2, paddingRight: 2 }}>
            <CytoscapeComponent
              elements={graphData}
              style={{ width: "100vw", height: "70vh" }}
              zoomingEnabled={true}
              maxZoom={6}
              minZoom={0.3}
              autounselectify={false}
              boxSelectionEnabled={false}
              layout={layout}
              stylesheet={defaultStyleSheet}
              cy={(cy) => {
                cyRef.current = cy
                defineListeners(cy)
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper style={{
              minWidth: "150px",
              background: "rgba(255,255,255,.9)",
              maxHeight: "100vh",
              overflow: "hidden",
              overflowY: "auto",
              paddingLeft: "18px",
              paddingRight: "18px",
              marginRight: "20px"
            }}>
              <CytokineLegend />
              <Typography gutterBottom variant='body1'>
                Filter on Weight
              </Typography>
              <Slider
                sx={{ color: '#1c345c' }}
                getAriaLabel={() => 'Weight range'}
                value={weightRange}
                onChange={onChange}
                onChangeCommitted={onChangeCommitted}
                valueLabelDisplay="auto"
                min={sliderMin}
                max={sliderMax}
                step={0.1}
                marks={[
                  { 'value': sliderMin, 'label': `${(sliderMin - 0.1).toFixed(1)}` },
                  { 'value': sliderMax, 'label': `${(sliderMax + 0.1).toFixed(1)}` }
                ]}
              />
              <GeneInformation selectedGenes={selectedGenes} />
            </Paper>
            <div style={{ marginRight: "20px" }}>
              <Button
                size="medium"
                variant='outlined'
                sx={{ width: "100%", mt: 2, }}
                startIcon={<SaveIcon htmlColor={COLOR_DARK} />}
                onClick={() => {
                  if (edgesCSV) { edgesCSVLink.current?.link.click() }
                  if (nodesCSV) { nodesCSVLink.current?.link.click() }
                }}
              >
                Save CSV
              </Button>
              <CSVLink
                data={edgesCSV}
                filename={`${symbol?.label.replaceAll(' ', '_')}_edges.csv`}
                className="hidden"
                ref={edgesCSVLink}
                target="_blank"
              />
              <CSVLink
                data={nodesCSV}
                filename={`${symbol?.label.replaceAll(' ', '_')}_nodes.csv`}
                className="hidden"
                ref={nodesCSVLink}
                target="_blank"
              />
            </div>
          </Grid>
        </Grid>
      )
      }
      <Footer />
    </ThemeProvider >

  );
}

export default Cytokines;